// Updated AddProduct.jsx with AI API integration for description, FAQs, and ProductMedia
import React, { useEffect, useState } from "react";
import { useWatch } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, FormProvider } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  CCard,
  CCardBody,
  CRow,
  CCol,
  CButton,
  CForm,
  CSpinner,
} from "@coreui/react";
import Loader from "../../../components/common/Loader";
import {
  calculatePrice,
  clearAiState,
  clearProductDetail,
  createProduct,
  fetchCategories,
  fetchProductDetails,
  fetchSpecifications,
  fetchSubcategories,
  generateAiDescription,
  generateAiFaqs,
  updateProduct,
  aiEditImage,
  fetchMetalCode,
} from "../../../features/seller/sellerSlice.js";
import productValidationSchema from "./sections/ValidationSchema.js";
import BasicDetails from "./sections/BasicDetails";
import TechnicalSpecifications from "./sections/TechnicalSpecifications";
import ProductDescription from "./sections/ProductDescription";
import ShippingTerms from "./sections/ShippingTerms";
import PricingEstimation from "./sections/PricingEstimation";
import ProductMedia from "./sections/ProductMedia";
import Specifications from "./sections/Specifications";
import FAQs from "./sections/FAQs";
import AppToast from "../../../components/seller/AppToast.jsx";
import { editImage } from "../../../features/user/userSlice.js";
import BidsInformation from "./sections/BidsInformation.jsx";
import productDraftSchema from "./sections/productDraftSchema.js";
import AppLoader from "../../../components/seller/AppLoader.js";

const AddProduct = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  // console.log("user", user);
  const { detail: productToEdit } = useSelector(
    (state) => state.seller.product
  );

  const {
    description: aiDescription,
    faqs: aiFaqs,
    generatingDescription,
    generatingFaqs,
  } = useSelector((state) => state.seller.ai);

  const { editImageLoading, editImageError, editImageSuccess } = useSelector(
    (state) => state.user
  );

  const TermsAndConditionsURL =
    "https://scraptrade.com.au/pages/slug/terms-and-conditions-scraptrade-com-au";
  const metalCode = useSelector((state) => state.seller.metalCode);
  const categories = useSelector((s) => s.seller.categories.list);
  const subCategories = useSelector((s) => s.seller.subCategories.list);
  const subCategoriesLoading = useSelector((s) => s.seller.subCategories.loading);


  // console.log("Subcategories from Redux:", subCategories);
  const { kycList } = useSelector((s) => s.user);
  const kycType = kycList?.[0]?.kyc_type;
  const [uploadedImage, setUploadedImage] = useState(""); // for existing image storage
  const [imagePreview, setImagePreview] = useState(""); // for new selection preview
  const [videoPreview, setVideoPreview] = useState(""); // for main video.
  const formatBidDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return d.toISOString().slice(0, 16); // "YYYY-MM-DDTHH:mm"
  };

  const [isDraft, setIsDraft] = useState(false);
  const [useDraftSchema, setUseDraftSchema] = useState(false);

  const schema = React.useMemo(() => {
    return useDraftSchema
      ? productDraftSchema({ kycType })
      : productValidationSchema({ kycType, categories });
  }, [useDraftSchema, kycType, categories]);

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      category_id: "",
      subcategory_id: "",
      name: "",
      description: "",
      price: "",
      quantity: "",
      location: "",
      unit_of_measurement: "",
      // condition: "Used",
      incoterm: "",
      payment_term: "",
      lead_time: "",
      delivery_port: "",
      delivery_radius: "",
      seller_latitude: null,
      seller_longitude: null,

      // Specifications & FAQs arrays
      specifications: [],
      faqs: [],

      // Bidding fields
      // is_reverse_auction: false,
      allow_bidding: 1,
      minimum_asking_price: null,
      bid_increment: null,
      bid_closed_at: "",
      // reverse_close_price: "",
      auction_status: "open", // always open, read-only

      // Metal & waste-related
      metal_code: "",
      sorting_type: "",
      attachment: "", // renamed from attachment_waste
      waste_percentage: "",
      waste_range: "",
      toxic_level: "",
      hazard_types: [], // renamed from hazard_type
      hazard_description: "",

      // Pricing & measurement
      price_per_unit: "",
      weight: "",
      currency: "",
      estimated_price: "",
      calc_unit: "",
      calc_weight: "",

      // Media
      image: null,
      video: null,

      save_as_draft: 0,
      disclaimer: false,
    },
  });

  const { handleSubmit, reset, setValue, watch } = methods;
  const [toast, setToast] = useState({
    visible: false,
    type: "info",
    message: "",
    cta: null, // optional CTA button: { label, onClick }
  });
  const [submitting, setSubmitting] = useState(false);
  const [draftSubmitting, setDraftSubmitting] = useState(false);

  const selectedCategoryId = useWatch({
    control: methods.control,
    name: "category_id",
  });

  const isMetal = React.useMemo(() => {
    if (!selectedCategoryId || !categories || !Array.isArray(categories))
      return false;
    const cat = categories.find(
      (c) => String(c.id) === String(selectedCategoryId)
    );
    if (!cat) return false;
    const name = (cat.name || "").toLowerCase();
    // Show PricingEstimation for Non-Ferrous AND Precious Metal
    // Only hide it for Ferrous Metal
    return (
      name.includes("non-ferrous") ||
      name.includes("non ferrous") ||
      name.includes("precious")
    );
  }, [selectedCategoryId, categories]);

  // Fetch initial data
  useEffect(() => {
    dispatch(clearAiState());
    dispatch(fetchCategories());
    if (id) {
      dispatch(fetchProductDetails(id))
        .unwrap()
        .then((p) => {
          // console.log("fetchProductDetails API response:", p);
          const mapped = {
            id: p.id ?? "",
            category_id: p.category_id ?? p.category?.id ?? "",
            subcategory_id: p.subcategory_id ?? p.subcategory?.id ?? "",
            name: p.name ?? "",
            description: p.description ?? "",
            price: p.price ?? "",
            quantity: p.quantity ?? "",
            location: p.location ?? "",
            unit_of_measurement: p.unit_of_measurement ?? "",
            incoterm: p.incoterm ?? p.incoterms ?? "",
            payment_term: p.payment_term ?? p.payment_terms ?? "",
            lead_time: p.lead_time ?? "",
            delivery_port: p.delivery_port ?? "",
            delivery_radius: p.delivery_radius ?? "",
            seller_latitude: p.seller_latitude ?? "",
            seller_longitude: p.seller_longitude ?? "",
            metal_code: p.metal_code ?? "",
            sorting_type: p.sorting_type ?? "",
            attachment: p.attachment ?? "",
            waste_range: p.waste_range ?? "",
            hazard_description: p.hazard_description ?? "",
            hazard_types: p.hazard_types ?? [],
            toxic_level: p.toxic_level ?? "",
            specifications: Array.isArray(p.specifications)
              ? p.specifications.map((s) => ({
                field_id: s.field?.id ?? s.specification_field_id,
                label: s.field?.name ?? s.label ?? "",
                input_type: s.field?.input_type ?? "text",
                value: s.value ?? "",
              }))
              : [],
            faqs: p.faqs ?? [],

            // --- Bidding Fields ---
            // is_reverse_auction: p.is_reverse_auction ?? false,
            allow_bidding: Number(p.allow_bidding ?? 1), // Default to 1 if not available
            minimum_asking_price:
              p.allow_bidding === 0 ? null : p.minimum_asking_price ?? "",
            bid_increment: p.allow_bidding === 0 ? null : p.bid_increment ?? "",
            bid_closed_at:
              p.allow_bidding === 0 ? null : formatBidDate(p.bid_closed_at),
            auction_status:
              p.allow_bidding === 0 ? "closed" : p.auction_status ?? "open",

            // --- Pricing ---
            price_per_unit: p.price_per_unit ?? "",
            estimated_price: p.estimated_price ?? "",
            currency: p.currency || "",

            // --- Media ---
            image: null,
            existingImage: p.image_url || "",
            video: null,

            // --- Previews ---
            aiPreview: p.ai_image_url ?? "",
          };
          reset(mapped);
          // setValue("specifications", mapped.specifications);
          // set previews after reset
          setUploadedImage(p.image_url || "");
          setImagePreview(""); // keep preview blank for comparison
          setVideoPreview(p.video_url || "");
        });
    }
  }, [id, dispatch, reset]);

  // Handle category/subcategory dependent data
  const categoryValue = watch("category_id");
  useEffect(() => {
    if (categoryValue) {
      dispatch(fetchSubcategories(categoryValue));
    }
  }, [categoryValue, dispatch]);

  const subcategoryValue = watch("subcategory_id");
  useEffect(() => {
    if (subcategoryValue && !id) {
      setValue("specifications", []);
      dispatch(fetchSpecifications(subcategoryValue))
        .unwrap()
        .then((specs) => {
          const formatted = specs.map((spec) => ({
            field_id: spec.id,
            label: spec.name,
            input_type: spec.input_type || "text",
            value: "",
          }));
          setValue("specifications", formatted);
        });
    }
  }, [subcategoryValue, dispatch, setValue]);

  useEffect(() => {
    if (categoryValue && subcategoryValue) {
      dispatch(
        fetchMetalCode({
          category_id: categoryValue,
          subcategory_id: subcategoryValue,
        })
      )
        .unwrap()
        .then((res) => {
          if (res?.metal_code) {
            setValue("metal_code", res.metal_code);
          } else if (typeof res === "string") {
            // Some APIs just return the code string directly
            setValue("metal_code", res);
          }
        })
        .catch((err) => {
          console.error("Failed to fetch metal code:", err);
          setToast({
            visible: true,
            type: "error",
            message: "Failed to fetch metal code",
          });
        });
    }
  }, [categoryValue, subcategoryValue, dispatch, setValue]);

  // Update previews when files change
  const watchedImage = watch("image");
  const watchedVideo = watch("video");

  useEffect(() => {
    if (watchedImage?.[0])
      setImagePreview(URL.createObjectURL(watchedImage[0]));
  }, [watchedImage]);

  useEffect(() => {
    if (watchedVideo?.[0])
      setVideoPreview(URL.createObjectURL(watchedVideo[0]));
  }, [watchedVideo]);

  const allow = Number(watch("allow_bidding"));

  useEffect(() => {
    if (allow === 0) {
      methods.clearErrors([
        "minimum_asking_price",
        "bid_increment",
        "bid_closed_at",
        "currency",
        "auction_status",
      ]);

      // Optionally also clear their values
      setValue("minimum_asking_price", null);
      setValue("bid_increment", null);
      setValue("bid_closed_at", null);
      setValue("currency", "AUD");
    }
  }, [allow]);

  // AI Handlers
  const handleAiDescription = async () => {
    console.log("🔥 HANDLE AI DESCRIPTION CLICKED");

    const name = watch("name");
    const category_id = watch("category_id");
    const unit_of_measurement = watch("unit_of_measurement");
    const location = watch("location");

    console.log("category_id from form:", category_id);

    if (!name) {
      return setToast({
        visible: true,
        type: "info",
        message: "Enter product name first.",
      });
    }

    if (!category_id) {
      return setToast({
        visible: true,
        type: "info",
        message: "Please select category first.",
      });
    }

    const selectedCategory = categories.find(
      (c) => String(c.id) === String(category_id)
    );

    console.log("matched category:", selectedCategory);

    const payload = {
      name,
      category: selectedCategory?.name, //  now works
      category_id, //  include id too
      condition: "Used",
      quantity: 1,
      unit: unit_of_measurement || "kg",
      location: location || "",
    };

    console.log("🚀 FINAL AI PAYLOAD:", payload);

    dispatch(generateAiDescription(payload));
  };

  useEffect(() => {
    if (aiDescription) setValue("description", aiDescription);
  }, [aiDescription, setValue]);

  const handleAiFaqs = async () => {
    const name = watch("name");
    const category_id = watch("category_id");
    const weight = watch("weight");
    const unit_of_measurement = watch("unit_of_measurement");
    const location = watch("location");

    console.log("name:", name);
    if (!name)
      return setToast({
        visible: true,
        type: "info",
        message: "Enter product name first.",
      });

    const payload = {
      name,
      category: categories.find((c) => c.id === category_id)?.name || "",
      condition: "Used",
      quantity: weight || 1,
      unit: unit_of_measurement || "kg",
      location: location || "",
    };
    dispatch(generateAiFaqs(payload));
  };

  useEffect(() => {
    if (aiFaqs?.length) setValue("faqs", aiFaqs);
  }, [aiFaqs, setValue]);

  // const handleAiImageEdit = async (action) => {
  //   const imageFile = watch("image")?.[0];

  //   if (!imageFile) {
  //     return setToast({
  //       visible: true,
  //       type: "info",
  //       message: "Upload an image first.",
  //     });
  //   }

  //   const formData = new FormData();
  //   formData.append("image", imageFile);
  //   formData.append("action", action);

  //   const result = await dispatch(editImage(formData)).unwrap();
  //   console.log("🧠 AI IMAGE RESPONSE:", result);

  //   const editedUrl = result?.data?.image_base64;
  //   console.log(editedUrl);
  //   if (!editedUrl) return;

  //   //  SAME AS OLD CODE — convert URL → File
  //   const resp = await fetch(editedUrl);
  //   const blob = await resp.blob();
  //   const aiFile = new File([blob], "ai-edited.png", { type: blob.type });

  //   //  replace image in react-hook-form
  //   setValue("image", [aiFile], {
  //     shouldDirty: true,
  //     shouldTouch: true,
  //   });

  //   //  update preview
  //   setImagePreview(editedUrl);
  // };


  const handleAiImageEdit = async (action) => {
    const imageFile = watch("image")?.[0];

    if (!imageFile) {
      return setToast({
        visible: true,
        type: "info",
        message: "Upload an image first.",
      });
    }

    const formData = new FormData();
    formData.append("image", imageFile);
    formData.append("action", action);

    const result = await dispatch(editImage(formData)).unwrap();

    const editedBase64 = result?.data?.image_base64;
    if (!editedBase64) return;

    // ✅ SET AI PREVIEW (THIS WAS MISSING)
    setValue("aiPreview", editedBase64, {
      shouldDirty: true,
    });

    // ❌ DO NOT update imagePreview
    // ❌ DO NOT replace original image
  };


  const handleSaveAsDraft = () => {
    const data = watch();

    // Make sure these fields are filled out before saving as draft
    const isDraftable =
      data.name && data.category_id && data.subcategory_id && data.location;

    if (!isDraftable) {
      return setToast({
        visible: true,
        type: "info",
        message:
          "Please fill out the product name, category, subcategory, and location before saving as draft.",
      });
    }

    setUseDraftSchema(true);
    setIsDraft(true);

    // Call onSubmit with isDraftMode = true
    setTimeout(() => onSubmit(data, true), 1200);
  };

  const onSubmit = async (data, isDraftMode = false) => {
    // react-hook-form's handleSubmit calls onSubmit(data, event) — normalize to boolean
    // so a SyntheticBaseEvent passed as 2nd arg doesn't make isDraftMode truthy
    isDraftMode = isDraftMode === true;

    if (!isDraftMode) {
      if (!data.seller_latitude || !data.seller_longitude) {
        setToast({
          visible: true,
          type: "error",
          message: "Please select a valid location from the list.",
        });

        document.querySelector('input[name="location"]')?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });

        return;
      }
    }

    if (!isDraftMode) setSubmitting(true);

    try {
      // const isDraftMode = isDraft === true;
      // Remove existingImage, backend handles media
      const payload = { ...data };
      payload.currency = data.currency || "AUD";

      // payload.save_as_draft = isDraftMode ? 1 : 0;
      console.log("Final Draft Flag:", payload.save_as_draft);
      // Clean empty strings or undefined values from payload
      Object.keys(payload).forEach((key) => {
        if (payload[key] === "" || payload[key] === undefined) {
          payload[key] = null; // Convert empty string or undefined to null
        }
      });

      if (isDraftMode) {
        // setSubmitting(true);
        Object.keys(payload).forEach((key) => {
          if (
            payload[key] === null ||
            payload[key] === undefined ||
            payload[key] === ""
          ) {
            delete payload[key]; // Delete fields that are empty or undefined
          }
        });
        // Optional: Remove fields that backend may reject when empty
        // delete payload.category_id;
        // delete payload.subcategory_id;
        // delete payload.minimum_asking_price;
        // delete payload.bid_increment;
        // But keep whatever user filled in
      }
      payload.allow_bidding = Number(payload.allow_bidding);
      if (payload.allow_bidding === 0) {
        payload.minimum_asking_price = null;
        payload.bid_increment = null;
        payload.bid_closed_at = null;
        payload.auction_status = null; // auction_status might be omitted or set to a default value like 'closed'
      }

      // 👇 Decide which image to send
      // if (data.aiPreview) {
      //   // AI image exists → send AI image
      //   payload.image = data.aiPreview; // backend should accept URL
      // } else {
      //   // No AI image → original file stays
      //   payload.image = data.image;
      // }


      const base64ToFile = async (base64, filename = "ai-image.png") => {
        const res = await fetch(base64);
        const blob = await res.blob();

        console.log("Blob from base64:", blob);
        console.log("Filename:", filename);
        return new File([blob], filename, { type: blob.type });
      };

      if (data.aiPreview) {
        const aiFile = await base64ToFile(data.aiPreview);
        payload.image = [aiFile]; // Wrap in array
      } else {
        payload.image = data.image; // Keep as FileList or array
      }

      delete payload.existingImage;
      delete payload.aiPreview;
      delete payload.video_url;

      console.log("FINAL PAYLOAD BEFORE API:", payload);

      const response = id
        ? await dispatch(updateProduct({ id, data: payload })).unwrap()
        : await dispatch(createProduct(payload)).unwrap();
      console.log("Message:", response.message);
      const successMsg = response?.message || "Saved";

      // ── Verification CTA check (only for final submit, not drafts) ──
      let toastCta = null;
      // console.log("[CTA DEBUG] isDraftMode =", isDraftMode, "| user =", user);
      if (!isDraftMode) {
        // Use loose check to handle int 1, string "1", or boolean true (matches pattern in RequestOtp.jsx)
        const kycVal = user?.kyc_verified;
        const bankVal = user?.is_bank_verified;
        const isKycVerified = kycVal === 1 || kycVal === "1" || kycVal === true;
        const isBankVerified = bankVal === 1 || bankVal === "1" || bankVal === true;

        // Use addresses_count or fallback to checking the address string (some Redux states miss count)
        const hasAddress = (user?.addresses_count ?? 0) > 0 || !!user?.address || !!user?.addressLine1;

        // console.log("CTA Verification Check:", {
        //   kyc_verified: kycVal,
        //   is_bank_verified: bankVal,
        //   addresses_count: user?.addresses_count,
        //   address: user?.address,
        //   isKycVerified,
        //   isBankVerified,
        //   hasAddress,
        // });

        if (!isKycVerified || !isBankVerified || !hasAddress) {
          const missing = [];
          if (!isKycVerified) missing.push("KYC");
          if (!isBankVerified) missing.push("Bank/Payout");
          if (!hasAddress) missing.push("Address");

          // // OLD: Determine which verification section to link to first
          // const firstSection = !isKycVerified
          //   ? "kyc"
          //   : !isBankVerified
          //   ? "payout"
          //   : "address";

          toastCta = {
            label: "Verify Now",
            ctaUrl: "/seller/verification-list",
          };
        }
      }

      navigate("/seller/products", {
        state: {
          toast: {
            type: "success",
            message: successMsg,
            // ctaLabel + ctaUrl are serializable strings (functions cannot go through navigation state)
            ctaLabel: toastCta?.label ?? null,
            ctaUrl: toastCta?.ctaUrl ?? null,
          },
        },
      });
    } catch (err) {
      console.error("Submission error:", err);

      if (err.errors) console.table(err.errors);

      setToast({
        visible: true,
        type: "error",
        message:
          Object.values(err.errors || {})[0]?.[0] ||
          err.message ||
          "Failed to save",
      });
    } finally {
      if (!isDraftMode) {
        setSubmitting(false);
      }
    }
  };

  return (
    <div className="container-fluid px-3">
      <CRow className="align-items-center mt-4 mb-3">
        <CCol>
          <h4>{id ? "Edit Product" : "Add Product"}</h4>
        </CCol>
        <CCol className="text-end">
          <CButton color="secondary" onClick={() => navigate(-1)}>
            Back
          </CButton>
        </CCol>
      </CRow>

      <CCard className="border-0 shadow-none">
        <CCardBody>
          <FormProvider {...methods}>
            <CForm
              onSubmit={handleSubmit(onSubmit, (formErrors) => {
                console.error("Validation errors:", formErrors);
                setToast({
                  visible: true,
                  type: "error",
                  message: "Validation failed. Check for missing fields.",
                });
              })}
            >
              <BasicDetails
                categories={categories}
                subCategories={subCategories}
                setValue={setValue}
              />
              <TechnicalSpecifications isMetal={isMetal} />
              <ProductDescription
                onGenerateAi={handleAiDescription}
                aiLoading={generatingDescription}
              />
              <Specifications />
              {isMetal && (
                <PricingEstimation
                  subCategories={subCategories}
                  calculatePrice={async (payload) => {
                    try {
                      const resultAction = await dispatch(
                        calculatePrice(payload)
                      ).unwrap();
                      console.log("About to call calculatePrice API");
                      return resultAction;
                    } catch (err) {
                      throw err;
                    }
                  }}
                />
              )}
              <BidsInformation isMetal={isMetal} />
              <ShippingTerms setValue={setValue} />
              <ProductMedia
                onAiEdit={handleAiImageEdit}
                aiLoading={editImageLoading}
                uploadedImage={uploadedImage}
                imagePreview={imagePreview}
                setImagePreview={setImagePreview}
                videoPreview={videoPreview}
                setVideoPreview={setVideoPreview}
                kycType={kycType}
                isEditMode={!!id}
              />
              <FAQs onGenerateAi={handleAiFaqs} aiLoading={generatingFaqs} />

              <CRow className="mt-4">
                <CCol>
                  <div
                    style={{
                      padding: "12px 15px",
                      border: "1px solid #dcdcdc",
                      borderRadius: "6px",
                      background: "#f9f9f9",
                    }}
                  >
                    <div className="d-flex align-items-start">
                      <input
                        type="checkbox"
                        style={{ marginTop: "4px", marginRight: "10px" }}
                        {...methods.register("disclaimer", { required: true })}
                      />

                      <label style={{ fontSize: "0.9rem" }}>
                        {/* I confirm that all the information provided is true and
                        accurate to the best of my knowledge. I agree to the */}
                        By using this platform, I agree to the{" "}
                        <a
                          href={TermsAndConditionsURL}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            fontWeight: "600",
                            textDecoration: "underline",
                          }}
                        >
                          Terms & Conditions
                        </a>
                        .
                      </label>
                    </div>

                    {methods.formState.errors.disclaimer && (
                      <p
                        style={{
                          color: "red",
                          marginTop: "6px",
                          marginLeft: "26px",
                        }}
                      >
                        You must agree before submitting.
                      </p>
                    )}
                  </div>
                </CCol>
              </CRow>

              <CRow className="mt-5 align-items-center">
                <CCol className="text-start">
                  <CButton
                    color="info"
                    disabled={
                      !watch("name") ||
                      !watch("category_id") ||
                      !watch("subcategory_id") ||
                      !watch("location")
                    }
                    // variant="outline"
                    onClick={async () => {
                      setDraftSubmitting(true);

                      methods.setValue("save_as_draft", 1);
                      const data = methods.getValues();

                      await onSubmit(data, true);

                      setDraftSubmitting(false);
                    }}
                  >
                    Save as Draft
                  </CButton>
                </CCol>

                <CCol className="text-end">
                  <CButton
                    type="submit"
                    color="primary"
                    className="me-2"
                    disabled={submitting || !watch("disclaimer")}
                    onClick={() => methods.setValue("save_as_draft", 0)}
                  >
                    {submitting ? "Saving..." : "Save"}
                  </CButton>
                  <CButton color="secondary" onClick={() => navigate(-1)}>
                    Cancel
                  </CButton>
                </CCol>
              </CRow>
            </CForm>
          </FormProvider>
        </CCardBody>
      </CCard>

      <AppToast
        visible={toast.visible}
        setVisible={(v) => setToast((s) => ({ ...s, visible: v }))}
        type={toast.type}
        message={toast.message}
      />
      {subCategoriesLoading && <AppLoader fullScreen />}
    </div>
  );
};

export default AddProduct;
